import { Button, SelectInput, SelectInputItem, Textarea, TextField } from '@ads/components-react';
import {
  AcademicCapIcon,
  ArrowsRightLeftIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  MapPinIcon,
  PlusIcon,
  ScissorsIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  useGetApiV1ExamCompanyDropdown,
  useGetApiV1ExamComponentTypes,
} from '@/shared/api/generated';
import { SUBTYPE_TEXT_MAX_LENGTH } from '@/shared/lib/examItemSubtype';
import { FileUpload, RichTextEditor } from '@/shared/ui/atoms';

import { useQuestionFormOptions } from '../../../../hooks/useQuestionFormOptions';
import { AuthorAutocomplete } from '../../../molecules/AuthorAutocomplete';
import { DisciplineAutocomplete } from '../../../molecules/DisciplineAutocomplete';
import { KeyTermsInput } from '../../../molecules/KeyTermsInput';

import type { UseQuestionEditFormReturn } from '../useQuestionEditForm';
import { AlternativesTable } from './AlternativesTable';
import { FieldLabel } from './FieldLabel';
import { FormSection } from './FormSection';
import { SettingTile } from './SettingTile';

interface GeneralTabProps {
  readonly state: UseQuestionEditFormReturn;
}

export function GeneralTab({ state }: GeneralTabProps) {
  const { t } = useTranslation('questionLibrary');
  const {
    form,
    isChoiceType,
    isMultipleChoice,
    isNumericType,
    isGradeType,
    isAudioType,
    isAttachmentType,
    hasSubtype,
    hasModelAnswer,
    hasKeyTerms,
    alternatives,
    correctAlternativeIds,
    item,
    setItem,
    files,
    addFiles,
    removeFile,
    handleAddAlternative,
    handleDeleteAlternative,
    handleCorrectChange,
    notifyLabelChange,
    depAuthor,
    setDepAuthor,
  } = state;

  const companiesQuery = useGetApiV1ExamCompanyDropdown();
  const componentTypesQuery = useGetApiV1ExamComponentTypes();

  const { difficultyOptions, languageOptions, subtypeOptions } = useQuestionFormOptions();

  return (
    <div className="flex flex-col gap-xxs pt-xs">
      {/* ─── Conteúdo ─────────────────────────────────────────────────────── */}
      <FormSection title={t('edit.sections.content')}>
        {/* Tipo de questão */}
        <div data-field-error="componentType">
          <Controller
            name="componentType"
            control={form.control}
            render={({ field, fieldState }) => (
              <SelectInput
                label={`${t('edit.settings.fields.componentType')}*`}
                size="md"
                fullWidth
                hasError={Boolean(fieldState.error)}
                helper={fieldState.error?.message ? t(fieldState.error.message as never) : ''}
                placeholder={t('edit.settings.placeholders.componentType')}
                value={field.value != null ? String(field.value) : ''}
                onValueChange={v => field.onChange(v ? Number(v) : undefined)}
              >
                {(componentTypesQuery.data ?? []).map(c => (
                  <SelectInputItem
                    custom={{ root: 'text-nowrap' }}
                    key={c.key}
                    value={String(c.key ?? '')}
                  >
                    {c.value}
                  </SelectInputItem>
                ))}
              </SelectInput>
            )}
          />
        </div>

        {/* Subtipo — apenas para questões dissertativas (MultilineText) */}
        {hasSubtype && (
          <div data-field-error="subtype">
            <Controller
              name="subtype"
              control={form.control}
              render={({ field }) => (
                <SelectInput
                  label={t('edit.settings.fields.subtype')}
                  size="md"
                  fullWidth
                  placeholder={t('edit.settings.placeholders.subtype')}
                  value={field.value != null ? String(field.value) : ''}
                  onValueChange={v => field.onChange(v ? Number(v) : undefined)}
                >
                  {subtypeOptions.map(opt => (
                    <SelectInputItem
                      custom={{ root: 'text-nowrap' }}
                      key={opt.value}
                      value={opt.value}
                    >
                      {opt.label}
                    </SelectInputItem>
                  ))}
                </SelectInput>
              )}
            />
          </div>
        )}

        {/* Enunciado */}
        <div className="flex flex-col gap-nano" data-field-error="label">
          <FieldLabel>{t('edit.settings.fields.label')}*</FieldLabel>
          <Controller
            name="label"
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <RichTextEditor
                  value={String(field.value ?? '')}
                  onChange={v => {
                    field.onChange(v);
                    notifyLabelChange(v);
                  }}
                  placeholder={t('edit.settings.placeholders.label')}
                  minHeight={180}
                />
                {fieldState.error?.message && (
                  <span className="text-base-danger-600 ts-caption-xxs">
                    {t(fieldState.error.message as never)}
                  </span>
                )}
              </>
            )}
          />
        </div>

        {/* Informação */}
        <div className="flex flex-col gap-nano">
          <FieldLabel>{t('edit.settings.fields.information')}</FieldLabel>
          <Controller
            name="information"
            control={form.control}
            render={({ field }) => (
              <RichTextEditor
                value={String(field.value ?? '')}
                onChange={v => field.onChange(v)}
                placeholder={t('edit.settings.placeholders.information')}
                minHeight={140}
              />
            )}
          />
        </div>

        {/* Resposta-modelo / Notas de Ensino — subtipos ModelAnswerEssay e MbaCase */}
        {hasModelAnswer && (
          <div data-field-error="modelAnswer">
            <Controller
              name="modelAnswer"
              control={form.control}
              render={({ field, fieldState }) => (
                <Textarea
                  label={`${t('edit.settings.fields.modelAnswer')}*`}
                  fullWidth
                  rows={5}
                  maxLength={SUBTYPE_TEXT_MAX_LENGTH}
                  placeholder={t('edit.settings.placeholders.modelAnswer')}
                  error={fieldState.error?.message ? t(fieldState.error.message as never) : ''}
                  value={String(field.value ?? '')}
                  onChange={e => field.onChange(e.target.value)}
                />
              )}
            />
          </div>
        )}

        {/* Termos-chave — apenas subtipo ModelAnswerEssay */}
        {hasKeyTerms && (
          <div data-field-error="keyTerms">
            <Controller
              name="keyTerms"
              control={form.control}
              render={({ field }) => (
                <KeyTermsInput
                  label={t('edit.settings.fields.keyTerms')}
                  helper={t('edit.settings.helpers.keyTerms')}
                  placeholder={t('edit.settings.placeholders.keyTerms')}
                  maxLength={SUBTYPE_TEXT_MAX_LENGTH}
                  value={String(field.value ?? '')}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        )}

        {/* Arquivo */}
        <div className="flex flex-col gap-nano">
          <FieldLabel>{t('edit.settings.fields.file')}</FieldLabel>
          <FileUpload files={files} addFiles={addFiles} removeFile={removeFile} />
        </div>

        {/* Numeric: min, max, resposta correta, casas decimais */}
        {isNumericType && (
          <div className="grid grid-cols-2 gap-xs md:grid-cols-4">
            <Controller
              name="min"
              control={form.control}
              render={({ field }) => (
                <TextField
                  label={t('edit.settings.fields.min')}
                  type="number"
                  size="md"
                  fullWidth
                  placeholder={t('edit.settings.placeholders.min')}
                  value={field.value != null ? String(field.value) : ''}
                  onChange={e =>
                    field.onChange(e.target.value ? Number(e.target.value) : undefined)
                  }
                />
              )}
            />
            <Controller
              name="max"
              control={form.control}
              render={({ field }) => (
                <TextField
                  label={t('edit.settings.fields.max')}
                  type="number"
                  fullWidth
                  size="md"
                  placeholder={t('edit.settings.placeholders.max')}
                  value={field.value != null ? String(field.value) : ''}
                  onChange={e =>
                    field.onChange(e.target.value ? Number(e.target.value) : undefined)
                  }
                />
              )}
            />
            <Controller
              name="correctAnswer"
              control={form.control}
              render={({ field }) => (
                <TextField
                  label={t('edit.settings.fields.correctAnswer')}
                  size="md"
                  fullWidth
                  placeholder={t('edit.settings.placeholders.correctAnswer')}
                  value={String(field.value ?? '')}
                  onChange={e => field.onChange(e.target.value)}
                />
              )}
            />
            <Controller
              name="decimalPlaces"
              control={form.control}
              render={({ field }) => (
                <TextField
                  label={t('edit.settings.fields.decimalPlaces')}
                  type="number"
                  size="md"
                  fullWidth
                  placeholder={t('edit.settings.placeholders.decimalPlaces')}
                  value={field.value != null ? String(field.value) : ''}
                  onChange={e =>
                    field.onChange(e.target.value ? Number(e.target.value) : undefined)
                  }
                />
              )}
            />
          </div>
        )}

        {/* Grade: min, max */}
        {isGradeType && (
          <div className="grid grid-cols-2 gap-xs">
            <Controller
              name="min"
              control={form.control}
              render={({ field }) => (
                <TextField
                  label={t('edit.settings.fields.min')}
                  type="number"
                  size="md"
                  fullWidth
                  placeholder={t('edit.settings.placeholders.min')}
                  value={field.value != null ? String(field.value) : ''}
                  onChange={e =>
                    field.onChange(e.target.value ? Number(e.target.value) : undefined)
                  }
                />
              )}
            />
            <Controller
              name="max"
              control={form.control}
              render={({ field }) => (
                <TextField
                  label={t('edit.settings.fields.max')}
                  type="number"
                  size="md"
                  fullWidth
                  placeholder={t('edit.settings.placeholders.max')}
                  value={field.value != null ? String(field.value) : ''}
                  onChange={e =>
                    field.onChange(e.target.value ? Number(e.target.value) : undefined)
                  }
                />
              )}
            />
          </div>
        )}

        {/* Audio: tempo máximo em segundos */}
        {isAudioType && (
          <Controller
            name="maxAudioTimeInSeconds"
            control={form.control}
            render={({ field }) => (
              <TextField
                label={t('edit.settings.fields.maxAudioTimeInSeconds')}
                type="number"
                size="md"
                fullWidth
                placeholder={t('edit.settings.placeholders.maxAudioTimeInSeconds')}
                value={field.value != null ? String(field.value) : ''}
                onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
              />
            )}
          />
        )}

        {/* Attachment: tamanho máximo em MB */}
        {isAttachmentType && (
          <Controller
            name="maxFileSizeInMegabytes"
            control={form.control}
            render={({ field }) => (
              <TextField
                label={t('edit.settings.fields.maxFileSizeInMegabytes')}
                type="number"
                size="md"
                fullWidth
                placeholder={t('edit.settings.placeholders.maxFileSizeInMegabytes')}
                value={field.value != null ? String(field.value) : ''}
                onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
              />
            )}
          />
        )}

        {/* Item + alternativas — apenas para tipos de escolha */}
        {isChoiceType && (
          <div className="flex flex-col gap-xs">
            <div className="flex flex-col gap-nano">
              <FieldLabel>{t('edit.settings.fields.item')}</FieldLabel>
              <RichTextEditor
                value={item}
                onChange={setItem}
                placeholder={t('edit.settings.placeholders.item')}
                minHeight={120}
              />
              <Button
                hierarchy="secondary"
                size="md"
                fullWidth
                leadingIcon={PlusIcon}
                onClick={handleAddAlternative}
              >
                {t('edit.alternatives.addItem')}
              </Button>
            </div>

            <AlternativesTable
              alternatives={alternatives}
              columnHeader={t('edit.alternatives.columnHeader')}
              deleteAriaLabel={t('edit.alternatives.deleteAriaLabel')}
              onDelete={handleDeleteAlternative}
              selectedCorrectIds={correctAlternativeIds}
              onCorrectChange={handleCorrectChange}
              isMultiple={isMultipleChoice}
            />
          </div>
        )}
      </FormSection>

      {/* ─── Classificação ────────────────────────────────────────────────── */}
      <FormSection title={t('edit.sections.classification')}>
        <div className="grid grid-cols-3 gap-xs">
          <AuthorAutocomplete
            value={depAuthor}
            onChange={id => setDepAuthor(id)}
            label={t('edit.dependency.author')}
            placeholder={t('edit.dependency.authorPlaceholder')}
          />
          <Controller
            name="companyId"
            control={form.control}
            render={({ field }) => (
              <SelectInput
                label={t('edit.settings.fields.company')}
                size="md"
                fullWidth
                value={field.value != null ? String(field.value) : ''}
                onValueChange={v => field.onChange(v ? Number(v) : undefined)}
                placeholder={t('edit.settings.placeholders.company')}
              >
                {(companiesQuery.data ?? []).map(c => (
                  <SelectInputItem
                    custom={{ root: 'text-nowrap' }}
                    key={c.key}
                    value={String(c.key ?? '')}
                  >
                    {c.value}
                  </SelectInputItem>
                ))}
              </SelectInput>
            )}
          />

          <Controller
            name="disciplineId"
            control={form.control}
            render={({ field }) => (
              <DisciplineAutocomplete
                label={t('edit.settings.fields.discipline')}
                placeholder={t('edit.settings.placeholders.discipline')}
                value={(field.value as number | null | undefined) ?? null}
                onChange={id => field.onChange(id ?? undefined)}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-3 gap-xs">
          <div data-field-error="weight">
            <Controller
              name="weight"
              control={form.control}
              render={({ field }) => (
                <TextField
                  label={`${t('edit.settings.fields.weight')}*`}
                  type="number"
                  size="md"
                  fullWidth
                  min={0}
                  placeholder={t('edit.settings.placeholders.weight')}
                  value={field.value != null ? String(field.value) : ''}
                  onChange={e =>
                    field.onChange(e.target.value ? Number(e.target.value) : undefined)
                  }
                />
              )}
            />
          </div>

          <div data-field-error="level">
            <Controller
              name="level"
              control={form.control}
              render={({ field, fieldState }) => (
                <SelectInput
                  label={`${t('edit.settings.fields.difficulty')}*`}
                  size="md"
                  fullWidth
                  hasError={Boolean(fieldState.error)}
                  helper={fieldState.error?.message ? t(fieldState.error.message as never) : ''}
                  placeholder={t('edit.settings.placeholders.difficulty')}
                  value={field.value != null ? String(field.value) : ''}
                  onValueChange={v => field.onChange(v ? Number(v) : undefined)}
                >
                  {difficultyOptions.map(opt => (
                    <SelectInputItem
                      custom={{ root: 'text-nowrap' }}
                      key={opt.value}
                      value={opt.value}
                    >
                      {opt.label}
                    </SelectInputItem>
                  ))}
                </SelectInput>
              )}
            />
          </div>

          <Controller
            name="cultureInfo"
            control={form.control}
            render={({ field }) => (
              <SelectInput
                label={t('edit.settings.fields.language')}
                size="md"
                fullWidth
                placeholder={t('edit.settings.placeholders.language')}
                value={String(field.value ?? '')}
                onValueChange={field.onChange}
              >
                {languageOptions.map(opt => (
                  <SelectInputItem
                    custom={{ root: 'text-nowrap' }}
                    key={opt.value}
                    value={opt.value}
                  >
                    {opt.label}
                  </SelectInputItem>
                ))}
              </SelectInput>
            )}
          />
        </div>
      </FormSection>

      {/* ─── Exibição e regras ────────────────────────────────────────────── */}
      <FormSection title={t('edit.sections.display')}>
        <div className="grid grid-cols-2 gap-xs">
          <Controller
            name="showLabel"
            control={form.control}
            render={({ field }) => (
              <SettingTile
                icon={TagIcon}
                label={t('edit.settings.fields.showLabel')}
                checked={(field.value as boolean) ?? false}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="showInformation"
            control={form.control}
            render={({ field }) => (
              <SettingTile
                icon={InformationCircleIcon}
                label={t('edit.settings.fields.showInformation')}
                checked={(field.value as boolean) ?? false}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="teacherEvaluation"
            control={form.control}
            render={({ field }) => (
              <SettingTile
                icon={AcademicCapIcon}
                label={t('edit.settings.fields.teacherEvaluation')}
                checked={(field.value as boolean) ?? false}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="required"
            control={form.control}
            render={({ field }) => (
              <SettingTile
                icon={ExclamationCircleIcon}
                label={t('edit.settings.fields.requiredField')}
                checked={(field.value as boolean) ?? false}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="fixed"
            control={form.control}
            render={({ field }) => (
              <SettingTile
                icon={MapPinIcon}
                label={t('edit.settings.fields.alwaysInEvaluation')}
                checked={(field.value as boolean) ?? false}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="pageBreak"
            control={form.control}
            render={({ field }) => (
              <SettingTile
                icon={ScissorsIcon}
                label={t('edit.settings.fields.pageBreak')}
                checked={(field.value as boolean) ?? false}
                onChange={field.onChange}
              />
            )}
          />
          {isChoiceType && (
            <Controller
              name="randomizeAlternatives"
              control={form.control}
              render={({ field }) => (
                <SettingTile
                  icon={ArrowsRightLeftIcon}
                  label={t('edit.settings.fields.randomizeAlternatives')}
                  checked={(field.value as boolean) ?? false}
                  onChange={field.onChange}
                />
              )}
            />
          )}
        </div>
      </FormSection>
    </div>
  );
}
